import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ChirpPropShapes from 'javascripts/shared/chirp_prop_shapes';

import { ChirpButton } from 'javascripts/components/chirp_button';
import { AudiobookItemButton } from 'javascripts/components/audiobook_item_button';
import { ShoppingCartContext } from 'javascripts/contexts/shopping_cart_context';

import { bubEvents } from 'javascripts/shared/events/bub_events';

import styles from 'stylesheets/components/add_to_cart_button';

export function AddToCartButton({
  audiobook,
  inCart,
  trackingCategory,
  trackingType,
  onClick,
  inWishlist,
  label,
  removeLabel,
  buttonStyle,
  buttonColor,
  hoverColor,
  fontSize,
  moveToWishlistOnFullCart,
  showAddToCartModal,
  suppressAddToCartError,
}) {
  const [isInCart, setIsInCart] = useState(inCart);

  const { addItemToCart, removeItemFromCart } = useContext(ShoppingCartContext);

  function onAddToCart() {
    const props = {
      label: 'Audiobook id',
      value: audiobook.id,
    };
    if (trackingType) {
      props.type = trackingType;
    }
    bubEvents.track(trackingCategory, `Clicked ${label} button`, props);

    addItemToCart(audiobook, {
      inWishlist,
      shouldRenderAddedToCartModal: showAddToCartModal,
      moveToWishlistOnFullCart,
      suppressErrorMessage: suppressAddToCartError,
    }).then(response => {
      if (response.inCart) {
        setIsInCart(true);
      }
      onClick();
    });
  }

  function onRemoveFromCart() {
    const props = {
      label: 'Audiobook id',
      value: audiobook.id,
    };
    if (trackingType) {
      props.type = trackingType;
    }
    bubEvents.track(trackingCategory, 'Clicked remove button', props);

    removeItemFromCart(audiobook).then(response => {
      if (!response.inCart) {
        setIsInCart(false);
      }
      onClick();
    });
  }

  useEffect(() => {
    setIsInCart(inCart);
  }, [inCart]);

  function renderAudiobookListButton() {
    if (isInCart) {
      return (
        <AudiobookItemButton
          className='addToCartButton'
          href='/shopping_cart'
          label='Added to Cart'
          buttonColor={buttonColor}
          hoverColor={hoverColor}
        />
      );
    }
    return (
      <AudiobookItemButton
        className='addToCartButton'
        onClick={onAddToCart}
        label={label}
        buttonColor={buttonColor}
        hoverColor={hoverColor}
      />
    );
  }

  function renderStickyFooterButton() {
    if (isInCart) {
      return (
        <ChirpButton
          displayType='block'
          displayStyle='yellow'
          class='addToCartButton'
          fullWidth
          href='/shopping_cart'
          label='Added to Cart'
        />
      );
    }
    return (
      <ChirpButton
        class='addToCartButton'
        displayType='block'
        displayStyle='yellow'
        fullWidth
        label={label}
        onClick={onAddToCart}
      />
    );
  }

  function renderLinkStyleButton() {
    return (
      <ChirpButton
        class='addToCartButton'
        displayStyle='underline'
        displayType='link'
        label={label}
        onClick={onAddToCart}
        fontSize={fontSize}
      />
    );
  }

  function renderStandardWithRemoveStyleButton() {
    const sizeClass = ( fontSize === 'small' ? styles.textSmall : styles.textXS );

    if (isInCart) {
      return (
        <ChirpButton
          class={sizeClass}
          displayStyle='gray'
          fullWidth
          label={removeLabel}
          onClick={onRemoveFromCart}
          fontSize={'small'}
        />
      );
    }
    return (
      <ChirpButton
        class={sizeClass}
        displayStyle='gray'
        fullWidth
        label={label}
        onClick={onAddToCart}
        fontSize={'small'}
      />
    );
  }

  function renderStandardCartButton(displayStyle) {
    if (isInCart) {
      return (
        <ChirpButton
          class='addToCartButton'
          displayStyle={displayStyle}
          fullWidth
          href='/shopping_cart'
          label='Added to Cart'
        />
      );
    }
    return (
      <ChirpButton
        class='addToCartButton'
        displayStyle={displayStyle}
        fullWidth
        label={label}
        onClick={onAddToCart} />
    );
  }

  function renderAddToCartButton() {
    if (buttonStyle === 'audiobookList') {
      return renderAudiobookListButton();
    }
    if (buttonStyle === 'stickyFooter') {
      return renderStickyFooterButton();
    }
    if (buttonStyle === 'link') {
      return renderLinkStyleButton();
    }
    if (buttonStyle === 'defaultRemove') {
      return renderStandardWithRemoveStyleButton();
    }
    if (buttonStyle === 'defaultYellow') {
      return renderStandardCartButton('yellow');
    }
    return renderStandardCartButton('gray');
  }

  return (
    <div className={styles.addToCartButton} >
      {renderAddToCartButton()}
    </div>);
}

AddToCartButton.propTypes = {
  audiobook: ChirpPropShapes.audiobook.isRequired,
  inCart: PropTypes.bool.isRequired,
  trackingCategory: PropTypes.string.isRequired,
  trackingType: PropTypes.string,
  onClick: PropTypes.func,
  inWishlist: PropTypes.bool,
  label: PropTypes.string,
  removeLabel: PropTypes.string,
  buttonStyle: PropTypes.oneOf(['default', 'defaultYellow', 'audiobookList', 'stickyFooter', 'link', 'defaultRemove']),
  buttonColor: PropTypes.string,
  hoverColor: PropTypes.string,
  fontSize: PropTypes.oneOf(['extraSmall', 'small']),
  moveToWishlistOnFullCart: PropTypes.bool,
  showAddToCartModal: PropTypes.bool,
  suppressAddToCartError: PropTypes.bool,
};

AddToCartButton.defaultProps = {
  onClick: _.noop,
  inWishlist: false,
  label: 'Add to Cart',
  removeLabel: 'Remove from Cart',
  buttonStyle: 'default',
  fontSize: 'extraSmall',
  moveToWishlistOnFullCart: true,
  showAddToCartModal: true,
  suppressAddToCartError: false,
};
